/**
 * Performance Monitoring Service
 * Tracks and reports application performance metrics
 */

import { ref, computed } from 'vue'

interface PerformanceMetric {
  name: string
  value: number
  unit: string
  timestamp: number
  category: 'navigation' | 'resource' | 'custom' | 'vue'
}

interface ResourceTiming {
  name: string
  duration: number
  size: number
  type: string
  cached: boolean
}

interface ComponentMetric {
  name: string
  renderTime: number
  updateTime: number
  mountTime: number
  instances: number
}

class PerformanceMonitor {
  private metrics = ref<PerformanceMetric[]>([])
  private componentMetrics = ref<Map<string, ComponentMetric>>(new Map())
  private resourceTimings = ref<ResourceTiming[]>([])
  private marks = new Map<string, number>()
  private observer: PerformanceObserver | null = null

  // Thresholds for performance warnings
  private thresholds = {
    pageLoad: 3000, // 3 seconds
    apiCall: 1000, // 1 second
    componentRender: 100, // 100ms
    bundleSize: 500 * 1024 // 500KB
  }

  constructor() {
    if (typeof window !== 'undefined' && 'performance' in window) {
      this.initializeObserver()
      this.collectInitialMetrics()
    }
  }

  /**
   * Initialize performance observer
   */
  private initializeObserver() {
    try {
      this.observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.processPerformanceEntry(entry)
        }
      })

      // Observe different entry types
      this.observer.observe({ entryTypes: ['navigation', 'resource', 'measure', 'paint'] })
    } catch (error) {
      console.warn('PerformanceObserver not supported:', error)
    }
  }

  /**
   * Process performance entries
   */
  private processPerformanceEntry(entry: PerformanceEntry) {
    switch (entry.entryType) {
      case 'navigation':
        this.processNavigationEntry(entry as PerformanceNavigationTiming)
        break
      case 'resource':
        this.processResourceEntry(entry as PerformanceResourceTiming)
        break
      case 'paint':
        this.processPaintEntry(entry)
        break
      case 'measure':
        this.processMeasureEntry(entry)
        break
    }
  }

  /**
   * Process navigation timing
   */
  private processNavigationEntry(entry: PerformanceNavigationTiming) {
    const metrics: PerformanceMetric[] = [
      {
        name: 'Page Load Time',
        value: entry.loadEventEnd - entry.fetchStart,
        unit: 'ms',
        timestamp: Date.now(),
        category: 'navigation'
      },
      {
        name: 'DOM Content Loaded',
        value: entry.domContentLoadedEventEnd - entry.fetchStart,
        unit: 'ms',
        timestamp: Date.now(),
        category: 'navigation'
      },
      {
        name: 'Time to First Byte',
        value: entry.responseStart - entry.fetchStart,
        unit: 'ms',
        timestamp: Date.now(),
        category: 'navigation'
      }
    ]

    this.metrics.value.push(...metrics)

    // Check thresholds
    if (metrics[0].value > this.thresholds.pageLoad) {
      console.warn(`Page load time (${metrics[0].value}ms) exceeds threshold (${this.thresholds.pageLoad}ms)`)
    }
  }

  /**
   * Process resource timing
   */
  private processResourceEntry(entry: PerformanceResourceTiming) {
    const timing: ResourceTiming = {
      name: entry.name,
      duration: entry.duration,
      size: entry.transferSize,
      type: this.getResourceType(entry.name),
      cached: entry.transferSize === 0 && entry.decodedBodySize > 0
    }

    this.resourceTimings.value.push(timing)

    // Check for large resources
    if (timing.size > this.thresholds.bundleSize && timing.type === 'script') {
      console.warn(`Large bundle detected: ${timing.name} (${Math.round(timing.size / 1024)}KB)`)
    }
  }

  /**
   * Process paint timing
   */
  private processPaintEntry(entry: PerformanceEntry) {
    this.metrics.value.push({
      name: entry.name === 'first-paint' ? 'First Paint' : 'First Contentful Paint',
      value: entry.startTime,
      unit: 'ms',
      timestamp: Date.now(),
      category: 'navigation'
    })
  }

  /**
   * Process custom measures
   */
  private processMeasureEntry(entry: PerformanceEntry) {
    this.metrics.value.push({
      name: entry.name,
      value: entry.duration,
      unit: 'ms',
      timestamp: Date.now(),
      category: 'custom'
    })
  }

  /**
   * Collect initial metrics
   */
  private collectInitialMetrics() {
    // Get existing performance entries
    if (performance.getEntriesByType) {
      const navEntries = performance.getEntriesByType('navigation')
      const resourceEntries = performance.getEntriesByType('resource')
      const paintEntries = performance.getEntriesByType('paint')

      navEntries.forEach(entry => this.processPerformanceEntry(entry))
      resourceEntries.forEach(entry => this.processPerformanceEntry(entry))
      paintEntries.forEach(entry => this.processPerformanceEntry(entry))
    }

    // Collect memory usage if available
    if ('memory' in performance) {
      const memory = (performance as any).memory
      this.metrics.value.push({
        name: 'JS Heap Size',
        value: memory.usedJSHeapSize,
        unit: 'bytes',
        timestamp: Date.now(),
        category: 'custom'
      })
    }
  }

  /**
   * Mark a timing point
   */
  mark(name: string): void {
    this.marks.set(name, performance.now())
    performance.mark(name)
  }

  /**
   * Measure between two marks
   */
  measure(name: string, startMark: string, endMark?: string): number {
    try {
      if (endMark) {
        performance.measure(name, startMark, endMark)
      } else {
        const startTime = this.marks.get(startMark)
        if (startTime !== undefined) {
          const duration = performance.now() - startTime
          performance.measure(name, startMark)

          this.metrics.value.push({
            name,
            value: duration,
            unit: 'ms',
            timestamp: Date.now(),
            category: 'custom'
          })

          return duration
        }
      }

      const entries = performance.getEntriesByName(name, 'measure')
      return entries.length > 0 ? entries[entries.length - 1].duration : 0
    } catch (error) {
      console.error('Error measuring performance:', error)
      return 0
    }
  }

  /**
   * Track component performance
   */
  trackComponent(name: string, phase: 'mount' | 'update' | 'render', duration: number): void {
    const existing = this.componentMetrics.value.get(name) || {
      name,
      renderTime: 0,
      updateTime: 0,
      mountTime: 0,
      instances: 0
    }

    switch (phase) {
      case 'mount':
        existing.mountTime = duration
        existing.instances++
        break
      case 'update':
        existing.updateTime = (existing.updateTime + duration) / 2 // Average
        break
      case 'render':
        existing.renderTime = (existing.renderTime + duration) / 2 // Average
        break
    }

    this.componentMetrics.value.set(name, existing)

    // Check threshold
    if (duration > this.thresholds.componentRender) {
      console.warn(`Slow ${phase} for component ${name}: ${duration}ms`)
    }
  }

  /**
   * Track API call performance
   */
  trackApiCall(url: string, method: string, duration: number, status: number): void {
    const metric: PerformanceMetric = {
      name: `${method} ${url}`,
      value: duration,
      unit: 'ms',
      timestamp: Date.now(),
      category: 'custom'
    }

    this.metrics.value.push(metric)

    // Check threshold
    if (duration > this.thresholds.apiCall) {
      console.warn(`Slow API call: ${method} ${url} took ${duration}ms`)
    }

    // Track error rates
    if (status >= 400) {
      this.metrics.value.push({
        name: `API Error: ${method} ${url}`,
        value: status,
        unit: 'status',
        timestamp: Date.now(),
        category: 'custom'
      })
    }
  }

  /**
   * Get performance report
   */
  getReport() {
    const report = {
      metrics: this.metrics.value,
      components: Array.from(this.componentMetrics.value.values()),
      resources: this.resourceTimings.value,
      summary: this.getSummary(),
      warnings: this.getWarnings()
    }

    return report
  }

  /**
   * Get performance summary
   */
  private getSummary() {
    const metrics = this.metrics.value
    const resources = this.resourceTimings.value

    // Calculate averages and totals
    const pageLoadMetrics = metrics.filter(m => m.name === 'Page Load Time')
    const apiMetrics = metrics.filter(m => m.category === 'custom' && m.name.includes('api'))

    return {
      pageLoadTime: pageLoadMetrics.length > 0 ? pageLoadMetrics[0].value : 0,
      totalResources: resources.length,
      cachedResources: resources.filter(r => r.cached).length,
      totalResourceSize: resources.reduce((sum, r) => sum + r.size, 0),
      averageApiTime: apiMetrics.length > 0
        ? apiMetrics.reduce((sum, m) => sum + m.value, 0) / apiMetrics.length
        : 0,
      slowComponents: Array.from(this.componentMetrics.value.values())
        .filter(c => c.renderTime > this.thresholds.componentRender)
        .length
    }
  }

  /**
   * Get performance warnings
   */
  private getWarnings(): string[] {
    const warnings: string[] = []
    const summary = this.getSummary()

    if (summary.pageLoadTime > this.thresholds.pageLoad) {
      warnings.push(`Page load time (${summary.pageLoadTime}ms) exceeds recommended threshold`)
    }

    if (summary.totalResourceSize > 5 * 1024 * 1024) { // 5MB
      warnings.push(`Total resource size (${Math.round(summary.totalResourceSize / 1024 / 1024)}MB) is too large`)
    }

    if (summary.slowComponents > 0) {
      warnings.push(`${summary.slowComponents} components are rendering slowly`)
    }

    const largeBundles = this.resourceTimings.value
      .filter(r => r.type === 'script' && r.size > this.thresholds.bundleSize)

    if (largeBundles.length > 0) {
      warnings.push(`${largeBundles.length} JavaScript bundles exceed size threshold`)
    }

    return warnings
  }

  /**
   * Clear metrics
   */
  clear(): void {
    this.metrics.value = []
    this.componentMetrics.value.clear()
    this.resourceTimings.value = []
    this.marks.clear()
  }

  /**
   * Get resource type from URL
   */
  private getResourceType(url: string): string {
    if (url.includes('.js')) return 'script'
    if (url.includes('.css')) return 'style'
    if (url.includes('.png') || url.includes('.jpg') || url.includes('.jpeg') || url.includes('.gif')) return 'image'
    if (url.includes('.woff') || url.includes('.ttf')) return 'font'
    if (url.includes('/api/')) return 'api'
    return 'other'
  }

  /**
   * Export metrics for analysis
   */
  exportMetrics(): string {
    const report = this.getReport()
    return JSON.stringify(report, null, 2)
  }

  /**
   * Destroy observer
   */
  destroy(): void {
    this.observer?.disconnect()
    this.clear()
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor()

// Vue plugin
export const performancePlugin = {
  install(app: any) {
    // Track component performance
    app.mixin({
      beforeCreate() {
        this._mountStart = performance.now()
      },
      mounted() {
        if (this._mountStart) {
          const duration = performance.now() - this._mountStart
          performanceMonitor.trackComponent(
            this.$options.name || 'Anonymous',
            'mount',
            duration
          )
        }
      },
      beforeUpdate() {
        this._updateStart = performance.now()
      },
      updated() {
        if (this._updateStart) {
          const duration = performance.now() - this._updateStart
          performanceMonitor.trackComponent(
            this.$options.name || 'Anonymous',
            'update',
            duration
          )
        }
      }
    })

    // Provide global access
    app.config.globalProperties.$performance = performanceMonitor
  }
}
